import React, { useCallback, useEffect, useState } from 'react';
import { getPurchaseBatch } from '../inventoryApi';
import {
  Modal, COLORS, SEMANTIC, SpinnerBlock, ErrorBlock, StatusBadge,
  formatCurrency, formatDate,
} from './InventoryUIKit';
import InvoiceTable from './InvoiceTable';

/**
 * PurchaseBatchDetailsModal
 *
 * Fetches a single purchase batch (GET /inventory/purchase-batches/{id}/)
 * and shows purchase information, remaining/consumed/value figures, and
 * the FIFO consumption history (consumption log).
 *
 * @param {number|string} batchId
 * @param {() => void} onClose
 */
const InfoStat = ({ label, value, accent }) => (
  <div style={{ flex: '1 1 140px', minWidth: '120px' }}>
    <p style={{ margin: '0 0 4px 0', fontSize: '11px', color: COLORS.mediumText, textTransform: 'uppercase', letterSpacing: '0.03em' }}>{label}</p>
    <p style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: accent || COLORS.darkText }}>{value}</p>
  </div>
);

const deriveStatus = (batch) => {
  if (batch.status) return batch.status;
  const remaining = Number(batch.remaining_quantity ?? 0);
  const purchased = Number(batch.purchased_quantity ?? batch.quantity ?? 0);
  if (remaining <= 0) return 'Fully Consumed';
  if (remaining >= purchased) return 'Available';
  return 'Partially Consumed';
};

const PurchaseBatchDetailsModal = ({ batchId, onClose }) => {
  const [batch, setBatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchDetails = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getPurchaseBatch(batchId);
      setBatch(data);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to load purchase batch details.');
    } finally {
      setLoading(false);
    }
  }, [batchId]);

  useEffect(() => { fetchDetails(); }, [fetchDetails]);

  const purchasedQty = Number(batch?.purchased_quantity ?? batch?.quantity ?? 0);
  const unitPrice = Number(batch?.unit_price ?? 0);
  const remainingQty = Number(batch?.remaining_quantity ?? 0);
  const consumedQty = batch?.consumed_quantity != null ? Number(batch.consumed_quantity) : Math.max(0, purchasedQty - remainingQty);
  const purchaseValue = batch?.purchase_value != null ? Number(batch.purchase_value) : purchasedQty * unitPrice;
  const remainingValue = batch?.remaining_value != null ? Number(batch.remaining_value) : remainingQty * unitPrice;

  const consumptionRows = (batch?.consumption_history || batch?.consumption_log || []).map((c, idx) => ({
    key: c.id ?? idx,
    quantity: c.quantity ?? c.quantity_used ?? 0,
    unit_price: c.unit_price ?? unitPrice,
    consumption_date: c.consumption_date ?? c.used_at ?? c.date ?? null,
  }));

  const consumptionColumns = [
    { key: 'quantity', label: 'Quantity', render: (r) => r.quantity },
    { key: 'unit_price', label: 'Unit Price', render: (r) => formatCurrency(r.unit_price) },
    { key: 'consumption_date', label: 'Consumption Date', render: (r) => formatDate(r.consumption_date) },
  ];

  return (
    <Modal
      title={batch ? `Batch · ${batch.invoice_number || `#${batch.id}`}` : 'Purchase Batch Details'}
      subtitle={batch ? `${batch.inventory_item_name || batch.inventory_item?.item_name || 'Inventory item'}` : undefined}
      onClose={onClose}
      width="760px"
    >
      {loading ? (
        <SpinnerBlock />
      ) : error ? (
        <ErrorBlock message={error} onRetry={fetchDetails} />
      ) : !batch ? (
        <ErrorBlock message="Purchase batch not found." />
      ) : (
        <>
          {/* Purchase Information */}
          <div style={{ marginBottom: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <h4 style={{ margin: 0, fontSize: '13px', fontWeight: '700', color: COLORS.mediumText, textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                Purchase Information
              </h4>
              <StatusBadge status={deriveStatus(batch)} />
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', backgroundColor: COLORS.lightGrey, borderRadius: '8px', padding: '16px 18px' }}>
              <InfoStat label="Invoice Number" value={batch.invoice_number || '—'} />
              <InfoStat label="Purchase Date" value={formatDate(batch.purchase_date)} />
              <InfoStat label="Supplier" value={batch.supplier || '—'} />
              <InfoStat label="Inventory Item" value={batch.inventory_item_name || batch.inventory_item?.item_name || '—'} />
              <InfoStat label="Purchased Quantity" value={purchasedQty} />
              <InfoStat label="Unit Price" value={formatCurrency(unitPrice)} />
            </div>
          </div>

          {/* Remaining / Consumed / Value cards */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginBottom: '24px' }}>
            <div style={{ flex: '1 1 160px', backgroundColor: SEMANTIC.greenLight, borderRadius: '8px', padding: '14px 16px' }}>
              <InfoStat label="Remaining Quantity" value={remainingQty} accent={SEMANTIC.green} />
            </div>
            <div style={{ flex: '1 1 160px', backgroundColor: SEMANTIC.redLight, borderRadius: '8px', padding: '14px 16px' }}>
              <InfoStat label="Consumed Quantity" value={consumedQty} accent={SEMANTIC.red} />
            </div>
            <div style={{ flex: '1 1 160px', backgroundColor: COLORS.lightGrey, borderRadius: '8px', padding: '14px 16px' }}>
              <InfoStat label="Purchase Value" value={formatCurrency(purchaseValue)} />
            </div>
            <div style={{ flex: '1 1 160px', backgroundColor: `${COLORS.aquaBlue}15`, borderRadius: '8px', padding: '14px 16px' }}>
              <InfoStat label="Remaining Value" value={formatCurrency(remainingValue)} accent={COLORS.aquaBlue} />
            </div>
          </div>

          {/* Consumption History */}
          <h4 style={{ margin: '0 0 10px 0', fontSize: '13px', fontWeight: '700', color: COLORS.mediumText, textTransform: 'uppercase', letterSpacing: '0.03em' }}>
            Consumption History
          </h4>
          <InvoiceTable
            columns={consumptionColumns}
            rows={consumptionRows}
            loading={false}
            error={null}
            emptyMessage="No FIFO deductions have been recorded against this batch yet."
            rowKey="key"
          />
        </>
      )}
    </Modal>
  );
};

export default PurchaseBatchDetailsModal;
